import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icon } from '@iconify/react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ConfirmationModal from '../../../components/common/ConfirmationModal';
import TableWrapper from '../../../components/common/TableWrapper';
import {
  adminDefaultSpacing,
  adminTherapistsListFilter,
} from '../../../defaults';
import {
  useGetTherapistsQuery,
  useResendEmailByAdminMutation,
  useToggleTherapistStatusMutation,
  useVerifyParentEmailMutation,
} from '../../../store/apis/user';
import { debounce, showToast } from '../../../utils/commonFunctions';

const Therapists = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearchValue, setFilterSearchValue] = useState('');
  const [anchorElAction, setAnchorElAction] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(null);
  const currentTherapist = useRef(null);
  const [verifyEmail] = useVerifyParentEmailMutation();

  const [resendVerification] = useResendEmailByAdminMutation();
  const filter = useMemo(() => {
    const filterObj = {
      page: Number(searchParams.get('page') || 1),
      limit: searchParams.get('limit') || 10,
      sort: searchParams.get('sort') || '',
      search: filterSearchValue,
    };

    if (searchParams.get('status') && searchParams.get('status') !== 'ALL') {
      filterObj.userStatus = searchParams.get('status');
    }
    return filterObj;
  }, [filterSearchValue, searchParams]);

  const setFilteredValue = useCallback((searchQuery) => {
    setFilterSearchValue(searchQuery);
  }, []);

  useEffect(() => {
    debounce(() => {
      setFilteredValue(searchQuery);
      if (filter?.page > 1 && searchQuery) setSearchParams({ page: 1 });
    }, 1000);
  }, [searchQuery, setFilteredValue]);

  const handleChangePage = (event, newPage) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchParams((prev) => {
      prev.set('page', newPage + 1);
      return prev;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchParams({
      limit: parseInt(event.target.value, 10),
      page: 1,
    });
  };
  const handleToggleStatus = async () => {
    const res = await toggleTherapistStatus({
      body: {
        status:
          currentTherapist.current?.status === 'TERMINATED' ||
          currentTherapist.current?.status === 'BLOCKED'
            ? 'ACTIVE'
            : openConfirmModal,
      },
      id: currentTherapist.current?._id,
    });
    if (res?.error) {
      showToast({ type: 'error', message: res.error?.data?.message });
      return;
    }
    showToast({
      type: 'success',
      message: 'Therapist status changed successfully',
    });
    setOpenConfirmModal(null);
  };

  const handleCloseAction = (event) => {
    event.stopPropagation();
    setAnchorElAction(null);
  };

  const {
    data: therapists,
    isLoading,
    isFetching,
  } = useGetTherapistsQuery({ filter });

  const [toggleTherapistStatus, { isLoading: toggleStatusLoading }] =
    useToggleTherapistStatusMutation();
  const navigate = useNavigate();

  const therapistStatus = (row) => {
    const status =
      row?.status === 'DELETED'
        ? 'deleted'
        : row?.status === 'TERMINATED'
          ? 'terminated'
          : row?.status === 'BLOCKED'
            ? 'blocked'
            : row?.status === 'UNDER_REVERIFICATION'
              ? 'reVerify'
              : row?.status === 'REVERIFICATION_FAILED'
                ? 'reVerify failed'
                : ['NOT_STARTED', 'IN_PROGRESS'].includes(
                      row?.onboarding?.status
                    )
                  ? 'notOnboard'
                  : row?.onboarding?.adminStatus === 'REJECTED'
                    ? 'rejected'
                    : row?.onboarding?.adminStatus === 'APPROVED'
                      ? 'verified'
                      : row?.onboarding?.adminStatus === 'PENDING'
                        ? 'pending'
                        : 'onboard';

    let color = '';
    let text = '';
    let textColor = '';
    switch (status) {
      case 'terminated':
        color = 'error.light';
        text = 'Terminated';
        textColor = 'error.darker';
        break;
      case 'deleted':
        color = 'error.light';
        text = 'Deleted';
        textColor = 'error.darker';
        break;
      case 'blocked':
        color = 'error.light';
        text = 'Blocked';
        textColor = 'error.darker';
        break;
      case 'reVerify':
        color = 'secondary.light';
        textColor = 'secondary.darker';
        text = 'Reverification Required';
        break;
      case 'reVerify failed':
        color = 'error.light';
        textColor = 'error.darker';
        text = 'Reverification Rejected';
        break;
      case 'notOnboard':
        color = 'warning.light';
        text = 'Onboarding Pending';
        textColor = 'warning.darker';
        break;
      case 'pending':
        color = 'secondary.light';
        textColor = 'secondary.darker';
        text = 'Verification Pending';
        break;
      case 'rejected':
        color = 'error.light';
        textColor = 'error.darker';
        text = 'Rejected';
        break;
      case 'verified':
        color = 'primary.contrastText';
        textColor = 'primary.darker';
        text = 'Verified';
        break;
      case 'onboard':
        color = 'secondary.light';
        textColor = 'secondary.darker';
        text = 'Verification Pending';
        break;
      default:
        color = 'secondary.light';
        textColor = 'secondary.darker';
        text = 'Verification Pending';
    }
    return (
      <Chip
        variant="outlined"
        sx={{ backgroundColor: color, color: textColor }}
        label={text}
      />
    );
  };

  const actions = [
    {
      label:
        currentTherapist.current?.status === 'TERMINATED' ||
        currentTherapist.current?.status === 'BLOCKED' ? (
          <Stack direction={'row'} gap={2} alignItems={'center'}>
            <Icon
              fontSize={22}
              strokeWidth={0.5}
              icon={'tdesign:user-checked'}
            ></Icon>
            <Typography>Activate</Typography>
          </Stack>
        ) : (
          <Stack direction={'row'} gap={2} alignItems={'center'}>
            <Icon fontSize={22} icon={'tdesign:user-error-1'}></Icon>
            <Typography>Terminate Therapist</Typography>
          </Stack>
        ),
      onClick: function () {
        setOpenConfirmModal('TERMINATED');
      },
    },
  ];

  if (
    !currentTherapist.current?.isEmailVerified &&
    currentTherapist.current?.email
  ) {
    actions.push({
      label: (
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <Icon
            fontSize={22}
            icon={'icon-park-outline:email-successfully'}
          ></Icon>
          <Typography>Verify Email</Typography>
        </Stack>
      ),
      disabled: currentTherapist.current?.isEmailVerified,
      onClick: async function (e) {
        const res = await verifyEmail({
          id: currentTherapist?.current?._id,
          from: 'therapist',
        });
        if (res?.data?.status === 'SUCCESS') {
          showToast({
            type: 'success',
            message: res?.data?.message,
          });
        } else {
          showToast({
            type: 'error',
            message: res?.error?.data?.message,
          });
        }
        handleCloseAction(e);
      },
    });

    actions.push({
      label: (
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <Icon
            fontSize={22}
            strokeWidth={0.5}
            icon={'icon-park-outline:send-email'}
          ></Icon>
          <Typography>Resend Verification Email</Typography>
        </Stack>
      ),
      disabled: currentTherapist.current?.isEmailVerified,
      onClick: async function (e) {
        const res = await resendVerification({
          id: currentTherapist?.current?._id,
        });
        if (res?.data?.status === 'SUCCESS') {
          showToast({
            type: 'success',
            message: res?.data?.message,
          });
        } else {
          showToast({
            type: 'error',
            message: res?.error?.data?.message,
          });
        }
        handleCloseAction(e);
      },
    });
  }

  if (
    !currentTherapist.current?.status ||
    currentTherapist.current?.status === 'ACTIVE'
  ) {
    actions.splice(1, 0, {
      label: (
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <Icon
            fontSize={22}
            strokeWidth={0.5}
            icon={'tdesign:user-blocked'}
          ></Icon>
          <Typography>Block Therapist</Typography>
        </Stack>
      ),
      onClick: function () {
        setOpenConfirmModal('BLOCKED');
      },
    });
  }

  if (
    !['NOT_STARTED', 'IN_PROGRESS'].includes(
      currentTherapist.current?.onboarding?.status
    )
  ) {
    actions.splice(actions.length, 0, {
      label: (
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <Icon
            fontSize={22}
            strokeWidth={0.5}
            icon={'mdi:account-payment-outline'}
          />
          <Typography>Transactions</Typography>
        </Stack>
      ),
      onClick: function () {
        navigate(
          `/admin/therapists/${currentTherapist.current?._id}/transactions`
        );
      },
    });
  }

  const columns = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      format: (row) => (
        <Stack>
          <Tooltip placement="bottom-start" title={row?.name}>
            <Typography
              sx={{
                width: '10rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant="subtitle2"
              fontWeight={'fontWeightBold'}
            >
              {row?.name}
            </Typography>
          </Tooltip>
        </Stack>
      ),
    },
    {
      id: 'info.contactInfo',
      label: 'Contact Info',
      sx: { width: '10rem' },
      format: (row) => (
        <Stack width={'12rem'} direction={'column'}>
          {row?.email && (
            <Tooltip title={row?.email} width={'100%'}>
              <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                <Typography
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant="subtitle2"
                >
                  {row?.email}
                </Typography>
              </Stack>
            </Tooltip>
          )}
          {row?.phone && (
            <Stack direction={'row'} alignItems={'center'} gap={0.5}>
              <Typography
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                variant="subtitle2"
              >
                {row?.phone}
              </Typography>
            </Stack>
          )}
        </Stack>
      ),
    },
    {
      id: 'info.businessName',
      label: 'Business Name',
      sortable: true,
      format: (row) => row?.info?.businessName,
    },

    {
      id: 'qualifications',
      label: 'Qualifications',
      format: (row) => (
        <>
          <Tooltip
            title={row?.info?.qualifications?.map((d) => d.name).join(', ')}
          >
            <Typography
              sx={{
                width: '10rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant="subtitle2"
            >
              {row?.info?.qualifications?.map((d) => d.name).join(', ')}
            </Typography>
          </Tooltip>
        </>
      ),
    },
    {
      id: 'session.charge',
      label: 'Charge per Session',
      sortable: true,
      format: (row) => row?.session?.charge,
    },
    {
      id: 'status',
      label: 'Status',
      format: (row) => therapistStatus(row),
    },
    {
      id: 'actions',
      label: 'Actions',
      format: (row) => (
        <>
          <Stack
            sx={{ width: '100%', m: 'auto', cursor: 'pointer' }}
            alignItems={'center'}
          >
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                currentTherapist.current = row;
                setAnchorElAction(event.currentTarget);
              }}
              color="secondary"
            >
              <Icon fontSize={24} icon="charm:menu-kebab" />
            </IconButton>
          </Stack>
        </>
      ),
    },
  ];

  const { control } = useForm({
    defaultValues: {
      filter: '',
    },
    mode: 'onChange',
  });
  return (
    <Stack width={'100%'} px={adminDefaultSpacing}>
      <Stack
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        justifyContent={{ xs: 'center', lg: 'space-between' }}
        mb={3}
        gap={2}
        sx={{ flexDirection: { xs: 'column', lg: 'row' } }}
      >
        <Typography variant="h5">Therapists</Typography>
        <Stack
          gap={2}
          flexWrap={'wrap'}
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'center', md: 'space-between' }}
        >
          <TextField
            control={control}
            sx={{
              background: (theme) => theme.palette.background.lightBlue,
              borderRadius: '32px',
              minWidth: { xs: '100%', sm: '250px' },
            }}
            InputProps={{
              sx: {
                'borderRadius': '32px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              },
            }}
            select={true}
            value={searchParams.get('status') || 'ALL'}
            onChange={(e) => {
              setSearchParams((prev) => {
                prev.set('status', e.target.value);
                prev.set('page', 1);
                return prev;
              });
            }}
            options={adminTherapistsListFilter}
          >
            {adminTherapistsListFilter?.map((option, index) => (
              <MenuItem
                style={{ width: '100%' }}
                value={option?.value}
                key={option?.value}
              >
                {option?.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            name="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
            InputProps={{
              startAdornment: (
                <Icon
                  style={{ marginRight: '8px' }}
                  fontSize={24}
                  icon={'carbon:search'}
                />
              ),
              sx: {
                'minWidth': { xs: '100%', sm: '200px' },
                'backgroundColor': (theme) =>
                  theme.palette.background.lightBlue,
                'borderRadius': '32px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              },
            }}
            placeholder="Search"
          />
        </Stack>
      </Stack>
      {isLoading || isFetching ? (
        <Box
          width="100%"
          height="100vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : therapists?.data?.length ? (
        <Stack>
          <TableWrapper
            onRowClick={(row) => {
              navigate(`/admin/therapists/${row?._id}`);
            }}
            isCollapsible={false}
            rowKey={'_id'}
            rows={therapists?.data || []}
            columns={columns}
          />
          <TablePagination
            component="div"
            count={therapists?.metadata?.total}
            page={filter?.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={filter?.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Stack>
      ) : (
        <Box
          justifyContent={'center'}
          alignItems={'center'}
          display={'flex'}
          width={'100%'}
          py={10}
        >
          <Typography>No therapists found!</Typography>
        </Box>
      )}
      <Menu
        sx={{ mt: '12px' }}
        id="menu-appbar"
        anchorEl={anchorElAction}
        // anchorOrigin={{
        //   vertical: 'bottom',
        //   horizontal: 'left',
        // }}
        keepMounted
        transformOrigin={{
          vertical: 40,
          horizontal: 'right',
        }}
        open={Boolean(anchorElAction)}
        onClose={handleCloseAction}
        slotProps={{
          paper: {
            elevation: 2,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.32))',
            },
          },
        }}
      >
        {actions.map((action, index) => {
          return (
            <MenuItem
              key={index}
              onClick={(e) => {
                action.onClick(e);
                handleCloseAction(e);
              }}
            >
              {action.label}
            </MenuItem>
          );
        })}
      </Menu>
      <ConfirmationModal
        title={
          currentTherapist.current?.status === 'TERMINATED' ||
          currentTherapist.current?.status === 'BLOCKED'
            ? // eslint-disable-next-line quotes
              "Are you sure you want to activate this therapist's account?"
            : // eslint-disable-next-line quotes
              `Are you sure you want to ${openConfirmModal === 'BLOCKED' ? 'block' : 'terminate'} this therapist's account?`
        }
        isLoading={toggleStatusLoading}
        open={!!openConfirmModal}
        handleCancel={() => {
          setOpenConfirmModal(null);
          currentTherapist.current = null;
        }}
        handleSubmit={handleToggleStatus}
      />
    </Stack>
  );
};

export default Therapists;
